import Component from 'vue-class-component';
import TombolaBaseComponent from '@/components/base/base.component';
import { User } from '@/app-modules/store/modules/user';
import { IAuthenticationService, LoginRequest, UserModel } from '@/components/login/_domain';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import CountrySelectComponent from '@/components/login/_components/country-select';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { EncodedChatStatusUsers } from '@/app-modules/store/modules/chat-status-user-map';
import { ConfigurationKey, ConfigurationPaths, IChatModsConfiguration} from '@/app-modules/config';
import { IUserChatStatusService } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import { ITogglesService } from '@/app-modules/config/toggles-service';

@Component({
  name: 'LoginComponent',
  components: {
    countrySelect: CountrySelectComponent
  }
})
export default class LoginComponent extends TombolaBaseComponent {
  private user: User = new User();
  private userPreferences: UserPreferences = new UserPreferences();
  private encodedChatStatusUsers: EncodedChatStatusUsers = new EncodedChatStatusUsers();
  private countryBeforeLogin: string;
  private tombolaWebUsername: string = '';
  private passwordInputType: string = 'password';
  private isActiveDirectoryAuthenicationLiveFeatureToggleEnabled: boolean = false;
  private loginSpinnerVisible: boolean = false;

  private get userChatStatusService(): IUserChatStatusService{
    return this.$container.get(SERVICE_IDENTIFIERS.IUserChatStatusService);
  }

  protected get togglesService(): ITogglesService {
    return this.$container.get(SERVICE_IDENTIFIERS.ITogglesService);
  }

  private get isActiveDirectoryAuthenicationLive(): boolean {
    return this.isActiveDirectoryAuthenicationLiveFeatureToggleEnabled;
  }

  private beforeMount() {
    this.user = this.$tombolaStore.Get(User);
    this.userPreferences = this.$tombolaStore.Get(UserPreferences);

    this.encodedChatStatusUsers = this.$tombolaStore.Get(EncodedChatStatusUsers);
    this.$tombolaStore.Save(this.encodedChatStatusUsers.clear());

    this.user.username = this.userPreferences.username;

    this.countryBeforeLogin = this.userPreferences.country || 'UK';
    this.initialiseActiveDirectoryAuthenticationFeatureToggleState();
    this.togglesService.populateTogglesPreLogin();
  }

  private initialiseActiveDirectoryAuthenticationFeatureToggleState() {
    this.configService.getConfiguration().then((config) => {
      this.isActiveDirectoryAuthenicationLiveFeatureToggleEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.active_directory_authentication_islive) === 'true';
    });
  }

  private get authService(): IAuthenticationService {
    return this.$container.get(SERVICE_IDENTIFIERS.IAuthenticationService);
  }

  private async getConfiguration(): Promise<IChatModsConfiguration> {
    const configuration = await this.configService.getConfiguration();
    return configuration;
  }

  private loginFailure: boolean = false;
  private invalidPasswordWords: string = '';

  private async formSubmit(): Promise<void> {
    this.loginFailure = false;
    this.invalidPasswordWords = '';
    await this.$validator.validateAll();

    if (!this.$validator.errors.any()) {
      const loginUsernameUsed = this.user.username;
      const loggedIn: boolean = await this.login();
      this.loginFailure = !loggedIn;

      this.ClearExistingShortcutHints();
      this.IfRememberMeIsSelectedSaveUsernameToUserPreferences(loginUsernameUsed);
      this.SaveDefaultAlertWordsToUserPreferences((this.countryBeforeLogin !== this.userPreferences.country));

      if (loggedIn) {
        const configuration = await this.getConfiguration();
        if (configuration.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_active_directory_for_chat_user_roles_islive) === 'true') {
          this.$tombolaStore.Save(this.encodedChatStatusUsers.load(await this.userChatStatusService.fetchUserRolesFromServer(this.user)));
        }
        const useNewToggleEndPoint = await this.togglesService.isLive('CIC-494_UseNETToggleSystem');
        await this.togglesService.populateToggleStore(useNewToggleEndPoint);
        this.user.lastLogin = new Date().toDateString();

        delete this.user.password;
        const cmPrefix = await this.userChatStatusService.getCMPrefix();
        this.user.userChatStatus = await this.userChatStatusService.getChatStatusForUser(`${cmPrefix}-${this.user.username}`);
        this.user.isCsMember = await this.userChatStatusService.getUserCsStatus(this.user.username);
        this.$tombolaStore.Save(this.user);

      }

      if (loggedIn) {
        this.navigateHome();
      }
    }
  }

  private async login(): Promise<boolean> {
    this.loginSpinnerVisible = true;
    try {
      const request = this.buildLoginRequest();
      const response = await this.authService.login(request);
      if (response.IsSuccessful()) {
        this.invalidPasswordWords = await this.IsPasswordInvalid(this.user.password);
        if (this.invalidPasswordWords.length > 0) {
          return false;
        }

        this.populateUserFromResponseUserModel(response.result, this.user);
      }
      return response.IsSuccessful();
    }
    finally {
      this.loginSpinnerVisible = false;
    }
  }

  private populateUserFromResponseUserModel(userModel: UserModel, user: User): void {
    user.userId = userModel.UserId;
    user.crosscheckId = userModel.CrossCheck;
    user.jwt = userModel.Jwt;
    user.autoRestoreAllWindows = false;
    user.username = userModel.Username;
    user.adroles = userModel.ADRoles || [];
  }

  private buildLoginRequest(): LoginRequest {
    const $username = this.$refs.username as HTMLInputElement;
    const $password = this.$refs.password as HTMLInputElement;
    const $selectedCountry = this.$refs.selectedCountry as HTMLInputElement;
    const request = new LoginRequest({
      username: ($username.value || '').trim(),
      password: $password.value,
      selectedCountry: $selectedCountry.value.toLowerCase(),
      allowActiveDirectoryLoginAttempts: this.isActiveDirectoryAuthenicationLive
    });

    if (this.isActiveDirectoryAuthenicationLive) {
      request.correctLoginRequestCredentialsIfLoginWithEmailAttempted();
    }

    return request;
  }

  private navigateHome(): void {
    this.$router.replace('home');
  }

  private IfRememberMeIsSelectedSaveUsernameToUserPreferences(loginUsernameUsed: string) {
    this.userPreferences.username = this.userPreferences.rememberMe ? loginUsernameUsed : '';
    this.$tombolaStore.Save(this.userPreferences);
  }

  private SaveDefaultAlertWordsToUserPreferences(forceSave: boolean = false) {
    if (!this.userPreferences.alertWords || this.userPreferences.alertWords.length === 0 || forceSave) {
      this.userPreferences.alertWords = this.getDefaultAlertWords();
      this.$tombolaStore.Save(this.userPreferences);
    }
  }
  private ClearExistingShortcutHints() {
    this.$modalManager.clearShortcutHints();
  }

  private getDefaultAlertWords(): string[] {
    const defaultAlertWords = this.configService.getConfigurationItem(ConfigurationKey.DEFAULT_ALERT_WORDS);
    return defaultAlertWords.split(',');
  }

  private get forgottenPasswordUrl(): string {
    return this.configService.getConfigurationItem(ConfigurationKey.FORGOTTEN_PASSWORD_URL);
  }

  private get internationalChatmodsUiUrl(): string {
    return this.configService.getConfigurationItem(ConfigurationKey.INTERNATIONAL_UI_URL);
  }

  private async IsPasswordInvalid(password: string): Promise<string> {
    const words = await this.userChatStatusService.getInvalidWordsForPassword();
    let wordsFound = [];
    wordsFound = words.filter((word) => {
              if (password && password.toLowerCase().indexOf(word) !== -1) {
                return true;
              }
            });
    return wordsFound.join(', ');
  }

  public getPasswordInvalidWords(): string {
    const invalidPasswordMsg: string = this.$lang.login.invalidPassword;
    return invalidPasswordMsg.format({
      invalidPasswordWords: this.invalidPasswordWords
    });
  }

  private shouldShowPassword() {
    this.passwordInputType = this.passwordInputType === 'password' ? 'text' : 'password';
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/login/login.component.ts